import type { ObjectivePeriod } from '@/types/objective-periods';

import { ObjectivePeriodStatus } from '@/constants/ObjectivePeriodStatus';
import { getLocalizedText } from '@/utils/localization';

interface OverlappingPeriodsParams {
    startDate: string;
    endDate: string;
    existingPeriods: ObjectivePeriod[];
    /** The period being edited, which never overlaps itself. */
    currentPeriodId?: string;
}

/**
 * The names of the periods sharing a day with `[startDate, endDate]`. Two windows overlapping is
 * allowed and assumed -- an objective running across both shows under each -- so this is what the
 * form says out loud, never what it refuses.
 *
 * Drafts are left out for the reason the lens leaves them out (`usePeriodOptions`): a draft is
 * offered as no one's cycle, so nothing shows twice under it yet. Dates compare as `yyyy-MM-dd`,
 * which sorts chronologically.
 */
export const overlappingPeriodNames = ({
    startDate,
    endDate,
    existingPeriods,
    currentPeriodId,
}: OverlappingPeriodsParams): string[] => {
    if (!startDate || !endDate) {
        return [];
    }

    return existingPeriods
        .filter(
            (period) =>
                period.id !== currentPeriodId &&
                period.status !== ObjectivePeriodStatus.DRAFT &&
                period.startDate <= endDate &&
                startDate <= period.endDate,
        )
        .map((period) => getLocalizedText(period.name));
};
